import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, LogOut, LayoutGrid, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { usePermission, useAnyPermission } from "@/hooks/use-permission";
import { useUserAvatar, useMyCompany } from "@/hooks/queries";
import { useFinanceAccess } from "@/hooks/queries/finance";
import { useAssistanceCenterAccess } from "@/hooks/queries/assistance-center";
import { useFleetAccess } from "@/hooks/queries/fleet-management";
import { useConsultationAccess } from "@/hooks/queries/consultations";
import { useSalesAccess } from "@/hooks/queries/sales";
import { useBrandLogo } from "@/hooks/use-brand-logo";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { UserProfileDialog } from "@/components/user-profile-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isRestrictedAccountType } from "@/lib/account-type";
import { useAppMenuAccess } from "@/hooks/use-app-menu-access";

export const SIDEBAR_ITEMS = [
  { title: "Início", url: "/inicio", emoji: "🏠" },
  { title: "Mapa", url: "/", emoji: "🗺️" },
  { title: "Usuários", url: "/users", emoji: "👤" },
  { title: "Geocerca", url: "/geofences", emoji: "📍" },
  { title: "Veículos", url: "/vehicles", emoji: "🚚" },
  { title: "Relatórios", url: "/reports", emoji: "📊" },
  { title: "Financeiro", url: "/financeiro", emoji: "💵" },
  { title: "Assistência", url: "/assistencia", emoji: "🛻" },
  { title: "Gestão de Frota", url: "/frota", emoji: "🚛" },
  { title: "Consultas", url: "/consultas", emoji: "🔎" },
  { title: "Vendas", url: "/vendas", emoji: "🛒" },
  { title: "Agenda", url: "/agenda", emoji: "📅" },
  { title: "Câmera", url: "/camera", emoji: "📷" },
] as const;

// Uma ação por seção do menu Administração — usado só pra decidir se o
// item aparece na sidebar (ver useAnyPermission), a checagem fina de cada
// seção acontece na própria página (admin.index.tsx e cada rota filha).
const ADMIN_SECTION_ACTIONS = [
  "identidade_visual",
  "conta_integracoes",
  "comunicacao",
  "notificacoes",
  "formularios",
] as const;

// Itens da sidebar que o usuário logado enxerga — extraído do AppSidebar
// (2026-10-09) pra MenuAccessRedirect (__root.tsx) usar a MESMA regra ao
// mandar o funcionário pro primeiro menu liberado.
export function useSidebarNavItems() {
  const { gpsDriveUser } = useAuth();
  const canSeeAdmin = useAnyPermission("administracao", [...ADMIN_SECTION_ACTIONS]);
  // Bug real 2026-09-01: a sidebar mostrava Veículos/Usuários pra QUALQUER
  // usuário logado, mesmo sem nenhuma permissão ativa. Correção pedida
  // explicitamente: Mapa/Geocerca/Relatórios NUNCA somem do menu (mesmo sem
  // permissão nenhuma) — Mapa fica sem nenhum veículo (ver
  // MapPage#canViewDevices), Geocerca/Relatórios mostram a tela "Sem
  // permissão" ao abrir (ver GeofencesPageGate/ReportsPageGate). Só
  // Veículos/Usuários continuam somendo do menu quando negados.
  const canViewDevices = usePermission("devices", "view");
  const canViewUsers = usePermission("users", "view");
  // Agenda (2026-09-13): cliente (accountType "usuario") nunca vê este item —
  // só admin/funcionário, mesmo critério usado em mobile-app.tsx.
  const isClientAccount = isRestrictedAccountType(gpsDriveUser?.accountType);
  // Agenda/Câmera (2026-09-16): viraram permissão de módulo ativada pelo
  // Super Master por empresa (ver CompanyFormDialog em master.index.tsx),
  // além do critério de accountType acima. Enquanto myCompany ainda não
  // chegou, cai no mesmo default do backend (agenda=true, camera=false)
  // pra não esconder a Agenda de quem sempre teve acesso — useMyCompany
  // também é persistido (React Query persistence), então normalmente já
  // chega do cache no primeiro render.
  const { data: myCompany } = useMyCompany();
  const agendaModuleEnabled = myCompany?.modules.agenda ?? true;
  const cameraModuleEnabled = myCompany?.modules.camera ?? false;
  // Financeiro (2026-09-28): admin sempre, funcionário com "financeiro.view",
  // cliente nunca — decidido pelo backend (GET /finance/access).
  const { data: financeAccess } = useFinanceAccess(!!gpsDriveUser && !isClientAccount);
  // Assistência/guinchos (2026-10-16): mesmo critério do Financeiro.
  const { data: assistanceAccess } = useAssistanceCenterAccess(!!gpsDriveUser && !isClientAccount);
  // Gestão de Frota (2026-10-01): módulo "frota" ativo na empresa + admin,
  // ou funcionário/cliente com a permissão — decidido pelo backend.
  const frotaModuleEnabled = myCompany?.modules.frota ?? false;
  const { data: fleetAccess } = useFleetAccess(!!gpsDriveUser && frotaModuleEnabled);
  // Consultas (2026-10-04): admin ou permissão "consultas" — backend decide.
  const { data: consultationAccess } = useConsultationAccess(!!gpsDriveUser);
  // Vendas/Pré-contratos (2026-10-05): admin ou permissão "vendas" — backend decide.
  const { data: salesAccess } = useSalesAccess(!!gpsDriveUser && !isClientAccount);
  // Vistorias (2026-10-08): menu só dentro de Gestão de Frota → Vistorias
  // (pedido 08/10 — tirado da barra lateral).
  // Motorista (2026-10-08): só os menus liberados em Permissões → Menus do
  // app; Geocerca/Relatórios nunca (não fazem parte da tela dele).
  // Funcionário (2026-10-09): Permissões → Menus do sistema e Agenda.
  const menuAccess = useAppMenuAccess();
  const visibleItems = SIDEBAR_ITEMS.filter((item) => {
    if (item.url === "/vehicles") return canViewDevices && menuAccess.veiculos;
    if (item.url === "/users") return canViewUsers;
    if (item.url === "/agenda") return !isClientAccount && agendaModuleEnabled && menuAccess.agenda;
    if (item.url === "/camera") return !isClientAccount && cameraModuleEnabled && menuAccess.camera;
    if (item.url === "/financeiro") return !isClientAccount && !!financeAccess?.view;
    if (item.url === "/assistencia") return !isClientAccount && !!assistanceAccess?.view;
    if (item.url === "/frota") return frotaModuleEnabled && !!fleetAccess?.view;
    if (item.url === "/consultas") return !!consultationAccess?.view;
    if (item.url === "/vendas") return !isClientAccount && !!salesAccess?.view;
    if (item.url === "/") return menuAccess.mapa;
    if (menuAccess.isMotorista && (item.url === "/geofences" || item.url === "/reports")) return false;
    // Funcionário (2026-10-09): Menus do sistema escolhidos em Permissões.
    if (item.url === "/inicio") return menuAccess.inicio;
    if (item.url === "/geofences") return menuAccess.geocercas;
    if (item.url === "/reports") return menuAccess.relatorios;
    return true;
  });
  const navItems = canSeeAdmin
    ? [...visibleItems, { title: "Administração", url: "/admin", emoji: "🛠️" } as const]
    : visibleItems;
  return navItems;
}

export function AppSidebar() {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const [expanded, setExpanded] = useState(false);
  const [dark, setDark] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { branding, displayedLogoUrl } = useBrandLogo(dark);
  const { data: avatarData } = useUserAvatar(user?.id);
  const avatarUrl = avatarData?.imageUrls
    ? resolveGpsDriveUrl(avatarData.imageUrls["128"] ?? avatarData.imageUrls.original)
    : undefined;
  const [profileOpen, setProfileOpen] = useState(false);

  // Modo claro/escuro configurado em Administração > Identidade Visual >
  // Tema & Cor só define o PADRÃO inicial — depois disso, o botão de
  // alternar aqui embaixo (setDark) manda, nunca re-sincroniza sozinho (daí
  // o ref: sincroniza uma vez só, na primeira vez que branding chega).
  const themeSyncedRef = useRef(false);
  useEffect(() => {
    if (!themeSyncedRef.current && branding?.themeMode) {
      themeSyncedRef.current = true;
      setDark(branding.themeMode === "dark");
    }
  }, [branding?.themeMode]);

  const navItems = useSidebarNavItems();

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate({ to: "/login", replace: true });
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [dark]);

  return (
    <aside
      className={cn(
        "group/side relative z-30 flex h-screen shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out",
        expanded ? "w-60" : "w-16",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-label={expanded ? "Fechar menu" : "Abrir menu"}
        className="absolute -right-3 top-20 z-40 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-sidebar text-sidebar-foreground shadow-md transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      >
        {expanded ? (
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
        )}
      </button>

      <div className="relative flex h-16 items-center justify-center border-b border-sidebar-border px-2">
        <img
          src={displayedLogoUrl}
          alt="GPS Drive — Sistema de Rastreamento"
          className={cn(
            "max-w-none object-contain transition-all duration-150",
            !branding?.logoSize && (expanded ? "h-32 w-auto" : "h-20 w-20"),
          )}
          style={
            branding?.logoSize
              ? // Tamanho customizado (Administração > Identidade Visual >
                // Logotipos) só define a altura no estado expandido — no
                // estado recolhido (ícone quadrado), escala na mesma
                // proporção do tamanho padrão (80/128) pra não ficar
                // gigante num espaço pensado pra ser pequeno.
                expanded
                ? { height: branding.logoSize, width: "auto", imageRendering: "auto" }
                : {
                    height: Math.round(branding.logoSize * 0.625),
                    width: Math.round(branding.logoSize * 0.625),
                    imageRendering: "auto",
                  }
              : { imageRendering: "auto" }
          }
        />
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-3">
        {navItems.map((item) => {
          const active = pathname === item.url || pathname.startsWith(`${item.url}/`);
          const itemClassName = cn(
            "group/item relative flex h-10 w-full items-center gap-3 rounded-lg px-2.5 text-sm font-medium transition-colors",
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
          );
          const itemContent = (
            <>
              {active && (
                <span className="absolute left-0 top-1.5 h-7 w-0.5 rounded-r-full bg-sidebar-primary-foreground/80" />
              )}
              <span className="grid h-[1.15rem] w-[1.15rem] shrink-0 place-items-center text-[1.05rem] leading-none">
                {item.emoji}
              </span>
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-left transition-opacity duration-150",
                  expanded ? "opacity-100" : "pointer-events-none opacity-0",
                )}
              >
                {item.title}
              </span>
            </>
          );
          return (
            <div key={item.url}>
              {/* Agenda (2026-09-13) — módulo com casca própria, aberto como
                  página cheia (nunca iframe/navegador embutido): clicar
                  pergunta se abre na mesma aba (navegação SPA normal) ou numa
                  aba nova (window.open) — a nova aba reconhece a MESMA sessão
                  sozinha, via os cookies HttpOnly de sessão (Traccar + GPS
                  Drive Backend), sem precisar passar token nenhum na URL. */}
              {item.url === "/agenda" ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" aria-label={item.title} className={itemClassName}>
                      {itemContent}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="start" className="w-48">
                    <DropdownMenuItem onClick={() => navigate({ to: item.url })}>
                      <LayoutGrid className="h-4 w-4" />
                      Abrir Agenda
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
                    >
                      <ExternalLink className="h-4 w-4" />
                      Abrir em nova aba
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to={item.url} aria-label={item.title} className={itemClassName}>
                  {itemContent}
                </Link>
              )}
              {(item.url === "/users" || item.url === "/reports") && (
                <div className="my-2 h-[2px] w-full rounded-full bg-gradient-to-r from-sky-500/0 via-sky-500 to-sky-500/0" />
              )}
            </div>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-sidebar-border p-2">
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          aria-label="Sair"
          className="flex h-10 w-full items-center gap-3 rounded-lg px-2.5 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-60"
        >
          <span className="grid h-[1.15rem] w-[1.15rem] shrink-0 place-items-center">
            <LogOut className="h-[1.05rem] w-[1.05rem]" strokeWidth={2} />
          </span>
          <span
            className={cn(
              "truncate transition-opacity duration-150",
              expanded ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            {loggingOut ? "Saindo…" : "Sair"}
          </span>
        </button>
        <button
          onClick={() => setDark((d) => !d)}
          className="flex h-10 w-full items-center gap-3 rounded-lg px-2.5 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
        >
          <span className="grid h-[1.15rem] w-[1.15rem] shrink-0 place-items-center text-[1.05rem] leading-none">
            {dark ? "☀️" : "🌙"}
          </span>
          <span
            className={cn(
              "truncate transition-opacity duration-150",
              expanded ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            {dark ? "Tema claro" : "Tema escuro"}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setProfileOpen(true)}
          className="mt-1 flex h-12 w-full items-center gap-3 rounded-lg px-2.5 text-left transition-colors hover:bg-sidebar-accent/60"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-sidebar-accent text-sidebar-accent-foreground text-lg">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span>🧑</span>
            )}
          </div>
          <div
            className={cn(
              "flex min-w-0 flex-col transition-opacity duration-150",
              expanded ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            <span className="truncate text-sm font-medium">{user?.name ?? "Operador"}</span>
            <span className="truncate text-[11px] text-sidebar-primary">Ver perfil</span>
          </div>
        </button>
      </div>

      <UserProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </aside>
  );
}
